import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import greyLock from '../../assets/grey-lock-2.svg';
import { poweredByAICA as poweredByAica } from '../../assets/hostedassets';
import styles from './TabSelector.module.scss';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
    selectBankingPendingCount,
    selectBankingInFlightCount,
    selectBankingExtractedCount,
    selectBankingLoading,
    selectBankingCurrentOrgId,
} from '../../store/banking/selectors';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import {
    fetchTabLockStatus,
    setModulePollingList,
    setShowRefreshReport,
    setTaskTrackerMap,
    setTaskTrackerResolved,
    mergeCrossModuleTaskTrackerCache,
    resetTaskTrackerResolution,
} from '../../store/investor/action';
import RefreshIcon from '../../assets/Svg/RefreshIcon';
import { getAllTaskTrackerData } from '../../store/investor/investorApiHelper';
import {
    isManagementProfileFetchInFlight,
    isManagementProfileLocked,
    mergeCrossModuleCacheEntries,
} from '../../store/investor/taskTrackerResolution';
import {
    TAB_TO_TASK_TRACKER_MAP,
    TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW,
    TabModule,
    CROSS_MODULE_TASK_STEPS,
} from '../../utils/constants/commonConstants';
import { Button } from '../_custom';
import { dataNotAvailable } from '../../assets/hostedassets';
import { getCompanyOverviewData } from '../../store/dashboard/overview/service';
import { DATA_FETCH_STATUS } from '../../store/dashboard/constants';
import { Mixpanel } from '../../utils/mixpanel';
import { getSubdomain } from '../../utils/utils';
import { REFRESH_UNLOCK_POS } from '../../store/investor/constants';
import { AICA_UNLOCK_REFRESH_OPENED } from '../../utils/constants/mixpanelEvents/investorEvents';
import AlertEngine from '../../modules/Dashboard/components/AlertEngine/AlertEngine';
import ReconciliationsChip from '../../modules/Dashboard/components/Reconciliations/ReconciliationsChip';
import CompaniesNetworkButton from '../../modules/Dashboard/components/CompaniesNetwork/CompaniesNetworkButton';
import { useIsMobile } from '../_custom/Hooks/useIsMobile';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import { useVisibilityConfig, useFeatureVisibility } from '../../utils/useVisibilityConfig';
import { TAB_LOCK_COMPLETED_STATUSES } from '../../modules/Dashboard/DashboardModules/RelatedParties/relatedParty_constants';

import { TopbarReturn, useUploadData } from '../../modules/Company/UploadData/useUploadData';

import { USER_TYPE } from '../../utils/constants/user';
import { TAB_SELECTOR_LAYOUT } from './constants';
import type { TabSelectorLayout } from './types';
import MobileSectionTabBar from './components/MobileSectionTabBar/MobileSectionTabBar';
import TalkToUsModal from '../UnlockRefresh/TalkToUsModal';
import { UNLOCK_REFRESH_LABEL } from '../UnlockRefresh/constants';
import { useUnlockRefreshOpener } from '../UnlockRefresh/useUnlockRefreshOpener';

interface TabSelectorProps {
    tabs: Tab[];
    currentTab: string;
    setCurrentTab: Function;
    companyAccessType: string;
    unlockedAt: string;
    selectedCompany: any;
    layout?: TabSelectorLayout;
}

export interface Tab {
    id?: string;
    label?: string;
    link?: string;
}

function TabSelector({
    tabs = [],
    currentTab,
    setCurrentTab,
    companyAccessType,
    unlockedAt,
    selectedCompany,
    layout = TAB_SELECTOR_LAYOUT.SIDEBAR,
}: Readonly<TabSelectorProps>) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const user = useAppSelector((state) => state.user.userData);

    const dashboardConfig: any = useAppSelector((state) => state.investor.config);
    const tabLockStatus = useAppSelector((state) => state.investor.tabLockStatus);
    const permissions = useAppSelector((state) => state.investor.permissions);
    const taskTrackerMap = useAppSelector((state) => state.investor.taskTrackerMap);
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const extendedPollingModules = useAppSelector((state) => (state.investor as any).extendedPollingModules ?? []);
    const investorData = useAppSelector((state) => state.investor);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const selectedCompanyFromRedux = useAppSelector((state) => state.investor.selectedCompany);
    // Read at dispatch time, not from the pre-await closure, so this never spreads a stale snapshot over the fetched company.
    const selectedCompanyRef = useRef(selectedCompanyFromRedux);
    selectedCompanyRef.current = selectedCompanyFromRedux;
    // Which org the route is on right now — the closure below only knows the one its request was for.
    const currentOrgIdRef = useRef(params.id);
    currentOrgIdRef.current = params.id;

    const profileGenerationStatus = investorData?.selectedCompany?.profileGenerationStatus;

    const secondaryModuleList = dashboardConfig?.tabs?.filter((tab: any) => tab.category === 'secondary').map((tab: any) => tab.id);
    const primaryModuleList = dashboardConfig?.tabs?.filter((tab: any) => tab.category === 'primary').map((tab: any) => tab.id);
    const refreshableModuleList: string[] =
        dashboardConfig?.tabs?.filter((tab: any) => tab.refreshableModule === true).map((tab: any) => tab.id) || [];
    const onboardingTabs = permissions?.onboardingTabs;

    const [showTooltipIndex, setShowTooltipIndex] = useState<boolean | number>(false);
    const [showUnlockTooltip, setShowUnlockTooltip] = useState(false);
    const [primaryDataStatusMap, setPrimaryDataStatusMap] = useState<Map<string, string>>(new Map());

    // TabSelector doubles as the mobile overlay (RECUR-27903); the chips are desktop-only.
    const isMobile = useIsMobile();
    const { config: visibilityConfig, canWrite: canExtractNow } = useVisibilityConfig();
    const bankingExtractAllowed = canExtractNow(VISIBILITY_KEYS.BANKING.EXTRACT_NOW);
    const { unlockAicaVisibility, sidebarRelatedNetworkVisibility, reconDrawerVisibility } = useFeatureVisibility({
        unlockAicaVisibility: VISIBILITY_KEYS.UNLOCK_AICA.ENABLED,
        sidebarRelatedNetworkVisibility: VISIBILITY_KEYS.SIDEBAR.RELATED_NETWORK_ENABLE,
        reconDrawerVisibility: VISIBILITY_KEYS.RECONCILIATIONS.DRAWER_ENABLE,
    });

    const [overallStatus, setOverallStatus] = useState<string>('');
    const [timerText, setTimerText] = useState('');
    let timerRef: any = useRef(null);
    let refreshTimerRef: any = useRef(null);
    const lastTaskTrackerMapRef = useRef<{ [taskTrackerStep: string]: { status: string; dateCreated: number } }>({});
    // Explicit "has the tracker fetch for THIS org come back" signal — taskTrackerMap === {}
    // is genuinely ambiguous between "not fetched yet" and "fetched, org has no rows at all"
    // (a real dev org: 0 TaskTracker rows), so isEmpty(taskTrackerMap) can't tell them apart.
    // Set in the fetch effect below; reset whenever params.id changes so a new org doesn't
    // inherit the previous org's resolved state and flash its padlock before its own fetch lands.
    const [hasTaskTrackerResolved, setHasTaskTrackerResolved] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const triggerMain = searchParams.get('triggerMain');

    const isProfileLocked = profileGenerationStatus === 'LOCKED' && !selectedCompanyFromRedux?.unlockAt;

    // `reconciliation` has no sidebar row of its own — the Reconciliations chip above the tab list
    // owns it. Filtered once, so the tab never reaches the lock-status fetch either. Memoised
    // because that fetch effect keys off this array's identity.
    const sidebarTabs = useMemo(() => tabs.filter((tab) => tab?.id !== TabModule.reconciliation), [tabs]);

    useEffect(() => {
        if (isEmpty(taskTrackerMap)) return;
        lastTaskTrackerMapRef.current = mergeCrossModuleCacheEntries(lastTaskTrackerMapRef.current, taskTrackerMap);
        // Publish the same merge to Redux so other readers (e.g. RefreshReport) see the exact
        // same cache instead of maintaining their own, independently-updated copy.
        dispatch(mergeCrossModuleTaskTrackerCache(taskTrackerMap));
    }, [taskTrackerMap]);

    // Management-Profile-only: on first navigation, fetchAllTaskTrackerData (:552-570) hasn't
    // necessarily landed yet — that is "not yet known", not "locked", and isTabLocked below must
    // not conflate the two (that conflation is the reported bug: a real unlock reads as a false
    // padlock until data arrives). Keyed on hasTaskTrackerResolved, NOT on taskTrackerMap being
    // empty — an org can genuinely have zero TaskTracker rows, and isEmpty can't tell "not
    // fetched yet" apart from "fetched, and there's really nothing there" (the spinner-forever
    // regression). Once resolved, an empty map correctly falls through to the normal "no
    // evidence ⇒ locked" branch below. Scoped to this one tab because isTabLocked's tracker
    // branch is shared by every secondary tab, and widening this changes lock-flash behaviour
    // for all of them — wider blast radius than this ticket asks for.
    const isManagementProfileUnresolved = !hasTaskTrackerResolved;
    // Shared with RefreshReport's drawer via the same pure predicate — see
    // store/investor/taskTrackerResolution.ts — so the two surfaces can't diverge on whether
    // base-details or the DIN fetch is still running.
    const managementProfileFetchInFlight = isManagementProfileFetchInFlight(taskTrackerMap, lastTaskTrackerMapRef.current);

    const unlockTime = new Date(unlockedAt);
    const showTimer = new Date() < new Date(unlockTime.getTime() + 15 * 60 * 1000);

    const { shouldDisableUnlock, isTalkToUsOpen, openTalkToUs, closeTalkToUs, contactInput } = useUnlockRefreshOpener();
    const { openInviteBorrower, closeInviteBorrower, showInviteBorrower, companyConfig, openCapitalRequirementDrawer } = useUploadData({
        userType: USER_TYPE.COMPANY,
        mode: 'topbar',
    }) as TopbarReturn;

    const userType = user.accountType;

    const bankingPendingCount = useAppSelector(selectBankingPendingCount);
    const bankingInFlightCount = useAppSelector(selectBankingInFlightCount);
    const bankingExtractedCount = useAppSelector(selectBankingExtractedCount);
    const bankingLoading = useAppSelector(selectBankingLoading);
    const bankingCurrentOrgId = useAppSelector(selectBankingCurrentOrgId);
    // Only show banking indicators when the stored data belongs to the currently viewed org.
    // This prevents stale counts from a previous org appearing on the first render after switching.
    const bankingDataIsForCurrentOrg = bankingCurrentOrgId === params.id;
    const showBankingSpinner = bankingDataIsForCurrentOrg && bankingExtractAllowed && bankingInFlightCount > 0;
    const showBankingDot = bankingDataIsForCurrentOrg && bankingExtractAllowed && bankingPendingCount > 0 && !showBankingSpinner;
    const showBankingNoDataIcon =
        bankingDataIsForCurrentOrg &&
        !bankingLoading &&
        bankingPendingCount === 0 &&
        bankingInFlightCount === 0 &&
        bankingExtractedCount === 0;

    useEffect(() => {
        if (unlockedAt && showTimer) {
            timerRef.current = setInterval(() => {
                getTimer();
            }, 1000);
        }

        return () => {
            clearInterval(timerRef?.current);
        };
    }, [unlockedAt, showTimer]);

    useEffect(() => {
        if (sidebarTabs.length && params.id)
            dispatch(
                fetchTabLockStatus({
                    tabsList: sidebarTabs.map((tab) => tab?.id ?? ''),
                    assesseeOrgId: params.id,
                }),
            );
    }, [sidebarTabs, params.id]);

    const fetchCompanyOverviewdata = async () => {
        const requestedOrgId = params.id;
        try {
            const response = await getCompanyOverviewData({
                assesseeOrgId: requestedOrgId,
                investorOrgId: investorOrgId,
            });

            // The effect refires per org without cancelling, so a slower earlier org can land last.
            if (currentOrgIdRef.current !== requestedOrgId) return;

            const primaryDataStatusMap = response?.data?.data?.primaryDataStatusMap;
            const overallStatus = response?.data?.data?.overallStatus;

            setPrimaryDataStatusMap(primaryDataStatusMap ? new Map(Object.entries(primaryDataStatusMap)) : new Map());
            setOverallStatus(overallStatus || '');

            // Update selectedCompanyFromRedux in Redux state with overallStatus
            const latestSelectedCompany = selectedCompanyRef.current;
            const latestOrgId = (latestSelectedCompany as any)?.assesseeOrgId;
            // The pre-fetch stub carries no id and is safe to extend; a different org's company is not.
            if (latestSelectedCompany && (!latestOrgId || latestOrgId === requestedOrgId)) {
                dispatch({
                    type: 'INVESTOR_FETCH_SINGLE_COMPANY_DETAILS_SUCCESS',
                    payload: {
                        ...latestSelectedCompany,
                        overallStatus: overallStatus || '',
                    },
                });
            }
        } catch (error) {
            if (currentOrgIdRef.current !== requestedOrgId) return;
            console.error('Failed to fetch company overview data:', error);
            setPrimaryDataStatusMap(new Map());
            setOverallStatus('');
        }
    };

    useEffect(() => {
        if (params.id && investorOrgId) {
            fetchCompanyOverviewdata();
        }
    }, [params.id, investorOrgId, triggerMain]);

    /**
     * This function checks if all steps for a module are complete or failed.
     * It returns true if all steps are complete or failed, otherwise false.
     * This is used to stop the polling after all steps are complete or failed.
     *
     * @returns boolean - true if all steps are complete or failed, otherwise false.
     */
    const checkStepsCompleteOrFailed = () => {
        if (!isEmpty(taskTrackerMap)) {
            const steps = modulePollingList.map((module) => TAB_TO_TASK_TRACKER_MAP[module]).flat();
            return steps.every((step) => TAB_LOCK_COMPLETED_STATUSES.has(taskTrackerMap[step]?.status ?? ''));
        }
        return false;
    };

    /**
     * Updates the module polling list based on the task tracker map status.
     *
     * PROBLEM SOLVED: This function was previously removing secondary modules (compliance, legalChecks, etc.)
     * from the polling list immediately when their required tasks weren't found in the taskTrackerMap,
     * causing loader icons to remain stuck indefinitely.
     *
     * KEY CHANGES MADE:
     * 1. **Secondary Module Persistence**: Instead of removing modules when their required tasks aren't
     *    in the tracker yet, we now keep them in the polling list until the tasks appear and complete.
     *    This is crucial because secondary modules often have a delay between unlock/refresh trigger
     *    and when their tasks actually appear in the tracker.
     *
     * 2. **Proper Task Completion Check**: Only remove modules when ALL their required tasks are
     *    COMPLETE or FAILED, not when tasks are missing from tracker.
     *
     * 3. **Prevent Infinite Polling**: Added safeguard to remove modules that have no required
     *    steps defined (prevents infinite polling for misconfigured modules).
     *
     * 4. **Optimized Redux Dispatch**: Only dispatch modulePollingList updates when the list
     *    actually changes to prevent unnecessary re-renders.
     *
     * FLOW:
     * - For each module in modulePollingList:
     *   - If primary module is PROCESSED → remove from polling
     *   - If no required steps defined → remove (prevent infinite polling)
     *   - If required steps not in tracker yet → keep in polling (wait for tasks to appear)
     *   - If all required steps are COMPLETE/FAILED → remove from polling
     *
     * @param taskTrackerMap - The task tracker map containing task steps and their statuses
     *                        Structure: { [taskName]: { status: 'COMPLETE'|'FAILED'|'PROCESSING', dateCreated: number } }
     */
    const updateModulePollingList = (taskTrackerMap: { [taskTrackerStep: string]: { status: string; dateCreated: number } }) => {
        const safeMap = taskTrackerMap || {};
        const completeOrFailedSteps: string[] = [];
        for (const module of modulePollingList) {
            if (primaryDataStatusMap.has(module) && primaryDataStatusMap.get(module) === DATA_FETCH_STATUS.PROCESSED) {
                completeOrFailedSteps.push(module);
                continue;
            }

            const requiredSteps = TAB_TO_TASK_TRACKER_MAP[module] || [];

            if (requiredSteps.length === 0) {
                completeOrFailedSteps.push(module);
                continue;
            }

            const stepsInTracker = requiredSteps.filter((step) => step in safeMap);

            if (stepsInTracker.length === 0) {
                continue;
            }

            const stepStatuses = stepsInTracker.map((step) => taskTrackerMap[step].status);

            const isStepCompleteOrFailed = stepStatuses.every((status) => TAB_LOCK_COMPLETED_STATUSES.has(status));

            if (isStepCompleteOrFailed) {
                completeOrFailedSteps.push(module);
            }
        }

        const newModulePollingList = modulePollingList.filter((module) => !completeOrFailedSteps.includes(module));

        if (newModulePollingList.length !== modulePollingList.length) {
            dispatch(setModulePollingList(newModulePollingList));
        }
    };

    /**
     * Handles the task tracker callback with comprehensive timeout management.
     *
     * PROBLEM SOLVED: The original implementation lacked proper timeout mechanisms, leading to:
     * - Infinite polling scenarios when tasks never appeared in tracker
     * - Poor user experience with loaders that never disappeared
     * - No fallback mechanism for edge cases
     *
     * KEY IMPROVEMENTS ADDED:
     * 1. **Dual Timeout System**:
     *    - Overall timeout (2 minutes): Stops all polling regardless of status
     *    - Individual module timeout (90 seconds): Removes specific modules whose tasks don't appear
     *
     * 2. **Proper Interval Cleanup**: Ensures refreshTimerRef is properly cleared and nullified
     *    when polling stops, preventing memory leaks and ghost intervals.
     *
     * 3. **Graceful Degradation**: When timeouts occur, the system gracefully removes problematic
     *    modules from polling rather than hanging indefinitely.
     *
     * TIMEOUT REASONING:
     * - **90 seconds for individual modules**: Reasonable time for backend to process unlock/refresh
     *   requests and create the required tasks in tracker. If tasks don't appear by then,
     *   likely indicates a backend issue or configuration problem.
     *
     * - **2 minutes overall**: Maximum time users should wait for any polling operation.
     *   Beyond this, it's better to stop polling and let users try again or refresh the page.
     *
     * FLOW:
     * 1. Check if all steps are complete → stop polling
     * 2. Check overall timeout (2 min) → stop polling, clear all modules
     * 3. Check individual module timeouts (90 sec) → remove timed-out modules
     * 4. Continue with normal polling logic
     *
     * @param startTime - The timestamp when polling started (used for timeout calculations)
     * @param twoMinutesInMs - The overall timeout duration in milliseconds (2 minutes)
     * @param taskTrackerMap - The current task tracker map from the API response
     */
    const handleTaskTrackerCallback = (
        startTime: number,
        twoMinutesInMs: number,
        taskTrackerMap: { [taskTrackerStep: string]: { status: string; dateCreated: number } },
    ) => {
        const currentTime = Date.now();
        const elapsedTime = currentTime - startTime;

        // Check if overall polling should stop due to completion
        if (checkStepsCompleteOrFailed()) {
            clearInterval(refreshTimerRef?.current);
            refreshTimerRef.current = null;
            if (!isEmpty(modulePollingList)) dispatch(setModulePollingList([]));
            return;
        }

        // Check if overall polling should stop due to timeout (2 minutes)
        if (elapsedTime >= twoMinutesInMs) {
            clearInterval(refreshTimerRef?.current);
            refreshTimerRef.current = null;
            // Clear remaining modules from polling list
            dispatch(setModulePollingList([]));
            return;
        }

        // Check for modules that should timeout individually (90 seconds without their tasks appearing)
        const moduleTimeoutMs = 90 * 1000; // 90 seconds
        const modulesToTimeout: string[] = [];
        const safeMap = taskTrackerMap || {};

        for (const module of modulePollingList) {
            const requiredSteps = TAB_TO_TASK_TRACKER_MAP[module] || [];
            const stepsInTracker = requiredSteps.filter((step) => step in safeMap);

            // If no steps in tracker after 90 seconds, consider it a timeout
            if (stepsInTracker.length === 0 && elapsedTime >= moduleTimeoutMs) {
                modulesToTimeout.push(module);
            }
        }

        if (modulesToTimeout.length > 0) {
            const newModulePollingList = modulePollingList.filter((module) => !modulesToTimeout.includes(module));
            dispatch(setModulePollingList(newModulePollingList));

            // If no modules left, stop polling
            if (newModulePollingList.length === 0) {
                clearInterval(refreshTimerRef?.current);
                refreshTimerRef.current = null;
                return;
            }
        }

        updateModulePollingList(taskTrackerMap || {});
    };

    /**
     * Handles polling for primary modules (those tracked in primaryDataStatusMap).
     *
     * PROBLEM SOLVED: This function was previously called for all modules, including secondary ones,
     * causing premature removal of secondary modules from the polling list when no primary modules
     * were in processing state.
     *
     * KEY OPTIMIZATION MADE:
     * **Primary Module Filtering**: Added logic to only execute primary module polling when there
     * are actually primary modules in the modulePollingList. This prevents the function from
     * calling updateModulePollingList for secondary-only polling scenarios.
     *
     * WHY THIS MATTERS:
     * - Secondary modules (compliance, legalChecks, etc.) were being removed from polling
     *   immediately because this function would call updateModulePollingList when no primary
     *   modules were processing, even though secondary modules were still waiting for their
     *   tasks to appear in the tracker.
     *
     * - This separation ensures that primary and secondary module polling logic don't interfere
     *   with each other, allowing each to follow their appropriate lifecycle.
     *
     * FLOW:
     * 1. Check if required parameters exist → early return if not
     * 2. Filter modulePollingList to only primary modules → early return if none
     * 3. Check if primary modules are processing → start interval or update polling list
     *
     * @returns cleanup function for the interval (if created) or undefined
     */
    const handlePrimaryModulePolling = () => {
        if (!params.id || !investorOrgId || primaryDataStatusMap.size === 0) {
            return;
        }

        // Check if we have any primary modules in the polling list
        const primaryModulesInPolling = modulePollingList.filter((module) => primaryDataStatusMap.has(module));

        if (primaryModulesInPolling.length === 0) {
            return;
        }

        const hasProcessingModules = Array.from(primaryDataStatusMap.values()).some((status) => status === DATA_FETCH_STATUS.PROCESSING);

        if (!hasProcessingModules) {
            updateModulePollingList(taskTrackerMap || {});
            return;
        }

        const interval = setInterval(() => {
            fetchCompanyOverviewdata();
        }, 5000);

        return () => clearInterval(interval);
    };

    /**
     * Main polling management useEffect - handles task tracker data polling lifecycle.
     *
     * CRITICAL PROBLEM SOLVED: The original implementation had a fatal flaw where existing
     * intervals were preserved when modulePollingList changed, causing:
     * - Old intervals to continue polling for previous modules
     * - New modules to be ignored (skipped because interval already existed)
     * - Loaders to remain stuck indefinitely
     *
     * ROOT CAUSE FIX:
     * **Interval Replacement Strategy**: Instead of skipping when an interval exists,
     * we now CLEAR the existing interval and create a fresh one. This ensures that
     * polling always reflects the current modulePollingList state.
     *
     * BEFORE (Broken):
     * ```
     * if (refreshTimerRef?.current) {
     *     return; // Skip creating new interval - THIS WAS THE BUG
     * }
     * ```
     *
     * AFTER (Fixed):
     * ```
     * if (refreshTimerRef?.current) {
     *     clearInterval(refreshTimerRef.current);
     *     refreshTimerRef.current = null;
     * }
     * ```
     *
     * ADDITIONAL IMPROVEMENTS:
     * 1. **Immediate Execution**: Added immediate fetchAllTaskTrackerData call before starting
     *    the interval to provide instant feedback instead of waiting 5 seconds.
     *
     * 2. **Secondary Module Focus**: Only starts polling when modulePollingList contains
     *    secondary modules (not all primary), as primary modules have their own polling logic.
     *
     * 3. **Proper Cleanup**: Ensures interval is cleared and reference nullified in cleanup
     *    function to prevent memory leaks.
     *
     * DEPENDENCIES EXPLANATION:
     * - `modulePollingList`: When this changes, we need to restart polling for new modules
     * - `primaryDataStatusMap`: When this changes, affects whether we should poll secondary modules
     *
     * POLLING TRIGGER CONDITIONS:
     * - modulePollingList is not empty
     * - Valid params.id exists
     * - Not all modules in list are primary modules (allPrimaryModules = false)
     *
     * @dependencies [modulePollingList, primaryDataStatusMap]
     */
    useEffect(() => {
        // Clear existing interval if it exists
        if (refreshTimerRef?.current) {
            clearInterval(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }

        // Check if modulePollingList contains only keys present in primaryDataStatusMap
        const allPrimaryModules = !isEmpty(modulePollingList) && modulePollingList.every((module) => primaryDataStatusMap.has(module));

        const shouldStartPolling = !isEmpty(modulePollingList) && params.id && !allPrimaryModules;

        if (shouldStartPolling) {
            const startTime = Date.now();
            const twoMinutesInMs = 2 * 60 * 1000;

            fetchAllTaskTrackerData(params.id as string, () => {});

            refreshTimerRef.current = setInterval(() => {
                fetchAllTaskTrackerData(params.id as string, (taskTrackerMap) => {
                    handleTaskTrackerCallback(startTime, twoMinutesInMs, taskTrackerMap);
                });
            }, 5000);
        }

        return () => {
            if (refreshTimerRef?.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [modulePollingList, primaryDataStatusMap]);

    // Management Profile's spinner (managementProfileFetchInFlight, above) only updates on a
    // fresh taskTrackerMap fetch — without joining modulePollingList, a step that completes on
    // the backend after the initial fetch is never observed and the spinner runs forever. This
    // reuses the existing interval/sweep above instead of a second polling loop: once the step
    // goes terminal, updateModulePollingList removes managementProfile the same way it already
    // removes every other module.
    //
    // managementProfileJoinedPollingRef gives this episode ONE shot at the shared list: without
    // it, once the sweep's own 2-minute/completion cutoff removes managementProfile, this effect
    // re-fires on that same modulePollingList change and re-adds it — resetting the sweep's
    // startTime and defeating the 2-minute bound for as long as the step stays non-terminal. The
    // ref only resets when managementProfileFetchInFlight itself drops to false, i.e. a genuinely
    // new fetch episode (including after an org switch, since that resets taskTrackerMap).
    const managementProfileJoinedPollingRef = useRef(false);
    useEffect(() => {
        if (!managementProfileFetchInFlight) {
            managementProfileJoinedPollingRef.current = false;
            return;
        }
        if (!modulePollingList.includes(TabModule.MANAGEMENT_PROFILE) && !managementProfileJoinedPollingRef.current) {
            managementProfileJoinedPollingRef.current = true;
            dispatch(setModulePollingList([...modulePollingList, TabModule.MANAGEMENT_PROFILE]));
        }
    }, [managementProfileFetchInFlight, modulePollingList]);

    useEffect(() => {
        const cleanup = handlePrimaryModulePolling();
        return cleanup;
    }, [primaryDataStatusMap, params.id, investorOrgId, triggerMain]);

    // Reset ONLY on a genuine org switch (params.id) — never on a same-org triggerMain refresh,
    // which exists specifically to refetch without showing a loading state (see
    // UnlockOverviewSection.tsx's own triggerMain comment). lastTaskTrackerMapRef resets here
    // too: TabSelector is not remounted on an org switch (no key prop upstream), so without this
    // a CROSS_MODULE_TASK_STEPS-cached terminal status from the PREVIOUS org would leak into the
    // new org's lock computation and falsely unlock it.
    useEffect(() => {
        lastTaskTrackerMapRef.current = {};
        setHasTaskTrackerResolved(false);
        dispatch(resetTaskTrackerResolution());
        // Redux's taskTrackerMap itself isn't touched by resetTaskTrackerResolution — without this,
        // an error on the new org's fetch (getAllTaskTrackerData's onError never dispatches
        // setTaskTrackerMap) would leave the previous org's terminal data readable as evidence here.
        dispatch(setTaskTrackerMap({}));
    }, [params.id]);

    useEffect(() => {
        if (!params.id) return;
        fetchAllTaskTrackerData(params.id, () => {
            setHasTaskTrackerResolved(true);
            dispatch(setTaskTrackerResolved(true));
        });
    }, [params.id, triggerMain]);

    /**
     * This function fetches the task tracker data.
     * It fetches the task tracker data from the API and updates the task tracker map.
     *
     * @param orgId - The organization ID.
     * @param callbackFunction - The callback function to be called with the task tracker map.
     */
    const fetchAllTaskTrackerData = (
        orgId: string,
        callbackFunction: (taskTrackerMap: { [taskTrackerStep: string]: { status: string; dateCreated: number } }) => void,
    ) => {
        getAllTaskTrackerData(
            { orgId },
            {
                onSuccess: (response: any) => {
                    dispatch(setTaskTrackerMap(response));
                    callbackFunction(response);
                },
                onError: (error: any) => {
                    console.error('Failed to fetch task tracker data', error);
                    // Passing an empty object to the callback to prevent errors
                    callbackFunction({});
                },
            },
        );
    };

    const getTimer = () => {
        const timeRemaining = new Date(unlockTime.getTime() + 6 * 60 * 1000).getTime() - new Date().getTime();
        const minutes = Math.floor(timeRemaining / (1000 * 60));
        const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
        let dots = '';
        switch (seconds % 4) {
            case 0:
                dots = '...';
                break;
            case 1:
                dots = '..';
                break;
            case 2:
                dots = '.';
                break;
            default:
                break;
        }

        if (minutes <= 0 && seconds <= 0) clearInterval(timerRef?.current);
        else setTimerText(`${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`);

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>{`Generating${dots}`}</div>
                <div>{`${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`}</div>
            </div>
        );
    };

    const handleClick = (tab) => {
        if (tab?.id === TabModule.BANKING_AND_FINANCIALS && showBankingNoDataIcon) {
            return;
        }
        // isTabLocked returns false here so the sidebar doesn't show a false padlock while
        // genuinely unresolved (see isManagementProfileUnresolved) — but "not known to be
        // locked" is not "known to be unlocked". Without this, a click during that window
        // navigates straight into ManagementProfileTab, which has no route-level lock guard of
        // its own, and can land the user in an empty tab with no banner once the fetch resolves
        // locked. Row stays non-navigable (spinner, not a padlock) until it resolves.
        if (tab?.id === TabModule.MANAGEMENT_PROFILE && isManagementProfileUnresolved) {
            return;
        }
        if (!isTabLocked(tab?.id)) {
            setCurrentTab(tab);
            if (tab?.link) {
                navigate(tab?.link);
            }
        } else if (tab?.id === 'overview') {
            // Special case: Overview tab should always be navigable even if locked
            // because it has an unlock section on the page itself
            setCurrentTab(tab);
            if (tab?.link) {
                navigate(tab?.link);
            }
        } else if (secondaryModuleList.includes(tab?.id) || refreshableModuleList.includes(tab?.id)) {
            if (shouldDisableUnlock) return;
            if (unlockAicaVisibility.show) {
                openTalkToUs();
            } else {
                openRefreshReport();
            }

            Mixpanel.track(AICA_UNLOCK_REFRESH_OPENED, {
                userRole: user?.accountType,
                pnoPlatform: getSubdomain(),
                userId: user?.userId,
                currentUrl: window.location.href,
                organizationId: params.id,
                accessedFrom: REFRESH_UNLOCK_POS.LOCKED_MODULE,
            });
        }
    };

    const getPrimaryTabState = (tabId: string) => {
        const tasks = TAB_TO_TASK_TRACKER_MAP[tabId] || [];

        if (tasks.length === 0) {
            return {
                image: dataNotAvailable,
                message: 'Data not available, please connect or upload data to access this module',
            };
        }

        const allTasksExist = taskTrackerMap && tasks.every((task) => taskTrackerMap?.[task]);
        if (!allTasksExist) {
            return {
                image: greyLock,
                message: 'Locked, please unlock AICA to access this module.',
            };
        }

        return {
            image: dataNotAvailable,
            message: 'Data not available, please connect or upload data to access this module',
        };
    };

    const renderTabStatusIcon = (
        tabId: string,
        primaryDataStatusMap: Map<string, string>,
        isTabLocked: (id: string) => boolean,
        primaryModuleList: string[],
        modulePollingList: string[],
        getPrimaryTabState: (id: string) => { image: string } | undefined,
        greyLock: string,
        LoadingSpinner: React.FC<{ color: string; height: string }>,
    ) => {
        // RECUR-25620: banking tab renders its own yellow "!" dot + internal loader; skip this generic status icon.
        if (tabId === TabModule.BANKING_AND_FINANCIALS) {
            return null;
        }

        const isProcessing = primaryDataStatusMap.get(tabId) === DATA_FETCH_STATUS.PROCESSING;
        const hasStatus = primaryDataStatusMap.has(tabId);
        const isLocked = isTabLocked(tabId);
        const isPrimary = primaryModuleList.includes(tabId);
        const isPolling = modulePollingList.includes(tabId) || (extendedPollingModules as string[]).includes(tabId);
        const tabImage = isPrimary ? getPrimaryTabState(tabId)?.image : greyLock;
        // Reuses this same spinner for Management Profile while the tracker data hasn't
        // resolved yet, or while base-details/DIN is genuinely in flight — both cases where a
        // bare padlock (or a bare tab label) would be misleading.
        const isManagementProfileFetching =
            tabId === TabModule.MANAGEMENT_PROFILE && (isManagementProfileUnresolved || managementProfileFetchInFlight);

        if ((hasStatus && isProcessing) || (!hasStatus && isPolling) || isManagementProfileFetching) {
            return (
                <div className={styles.PollingIcon}>
                    <LoadingSpinner color="var(--primary-text-colour)" height="16px" />
                </div>
            );
        }

        if (isLocked) {
            return <img src={tabImage} alt="" height="16px" />;
        }

        return null;
    };

    const isTabLocked = useCallback(
        (tabId: string) => {
            // Priority check: If overview tab and overallStatus is LOCKED, always lock the overview tab
            if (tabId === 'overview' && overallStatus === 'LOCKED') {
                return true;
            }

            // RECUR-25620: banking tab is never unlock-gated; state is driven by banking slice.
            if (tabId === TabModule.BANKING_AND_FINANCIALS) {
                return false;
            }

            if (Array.isArray(secondaryModuleList) && !secondaryModuleList.includes(tabId)) {
                const polusEntry = tabLockStatus?.[tabId];
                if (polusEntry == null) return TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW.includes(tabId);
                return !!polusEntry.isLocked;
            }

            if (tabId === TabModule.MANAGEMENT_PROFILE) {
                // Shared with RefreshReport's drawer via the same pure predicate — see
                // store/investor/taskTrackerResolution.ts — so the two surfaces can't diverge.
                return isManagementProfileLocked(hasTaskTrackerResolved, taskTrackerMap, lastTaskTrackerMapRef.current, tabLockStatus);
            }

            const effectiveTaskTrackerMap = isEmpty(taskTrackerMap) ? lastTaskTrackerMapRef.current : taskTrackerMap;
            const cachedTaskTrackerMap = lastTaskTrackerMapRef.current;

            let anyStepCompleteOrFailed = false;
            if (!isEmpty(effectiveTaskTrackerMap)) {
                const taskTrackerSteps = (TAB_TO_TASK_TRACKER_MAP[tabId] || [])
                    .map((step) => {
                        const cachedStatus = cachedTaskTrackerMap[step]?.status;
                        if (CROSS_MODULE_TASK_STEPS.has(step) && TAB_LOCK_COMPLETED_STATUSES.has(cachedStatus ?? '')) return cachedStatus;
                        if (step in effectiveTaskTrackerMap) return effectiveTaskTrackerMap[step].status;
                        return cachedStatus ?? null;
                    })
                    .filter((status): status is string => status !== null);
                anyStepCompleteOrFailed = isEmpty(taskTrackerSteps)
                    ? false
                    : taskTrackerSteps.some((status) => TAB_LOCK_COMPLETED_STATUSES.has(status));
            }

            // Tabs that should check isProfileLocked condition
            const checkProfileLockedTabs = [TabModule.OVERVIEW];
            const isLockedByProfile =
                checkProfileLockedTabs.includes(tabId as TabModule) && isProfileLocked && !onboardingTabs?.includes(tabId);

            return !!(isLockedByProfile || !anyStepCompleteOrFailed || tabLockStatus?.[tabId]?.isLocked);
        },
        [isProfileLocked, onboardingTabs, taskTrackerMap, tabLockStatus, secondaryModuleList, overallStatus, hasTaskTrackerResolved],
    );

    const openRefreshReport = () => {
        dispatch(setShowRefreshReport(true));

        Mixpanel.track(AICA_UNLOCK_REFRESH_OPENED, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: params.id,
            accessedFrom: REFRESH_UNLOCK_POS.UNLOCKED_BUTTON,
        });
    };

    const handleUnlockRefreshClick = () => {
        if (unlockAicaVisibility.show) {
            openTalkToUs();
        } else {
            openRefreshReport();
        }
        Mixpanel.track(AICA_UNLOCK_REFRESH_OPENED, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: params.id,
            accessedFrom: REFRESH_UNLOCK_POS.UNLOCKED_BUTTON,
        });
    };

    const getLockedTabTooltipText = (tabId: TabModule) => {
        let message = '';
        if (tabId === TabModule.BANKING_AND_FINANCIALS) {
            if (showBankingDot) {
                return 'Files available for extraction, please extract the files to see the data';
            }
            return 'Data not available, please upload bank statements to access this module';
        }
        if (primaryModuleList.includes(tabId)) {
            return getPrimaryTabState(tabId)?.message;
        }
        switch (tabId) {
            case TabModule.OVERVIEW:
                message = 'Discover key relationships, risk levels, and actionable alerts';
                break;
            case TabModule.COMPLIANCES:
                message = 'Monitor GST and EPF filing compliances.';
                break;
            case TabModule.LEGAL_CHECKS:
                message = 'View legal cases involving the company or directors.';
                break;
            case TabModule.AUDITED_FINANCIALS:
                message = 'Analyse latest financial statements and ratios.';
                break;
            default:
                message = 'Locked, please unlock AICA to access this module.';
                break;
        }
        return tabLockStatus?.[tabId]?.lockedMessage ?? message;
    };

    if (layout === TAB_SELECTOR_LAYOUT.TAB_ROW) {
        return (
            <>
                <MobileSectionTabBar
                    tabs={tabs}
                    currentTab={currentTab}
                    onTabClick={handleClick}
                    renderStatusIcon={(tabId) =>
                        renderTabStatusIcon(
                            tabId,
                            primaryDataStatusMap,
                            isTabLocked,
                            primaryModuleList,
                            modulePollingList,
                            getPrimaryTabState,
                            greyLock,
                            LoadingSpinner,
                        )
                    }
                />
                <TalkToUsModal isOpen={isTalkToUsOpen} onClose={closeTalkToUs} contactInput={contactInput} />
            </>
        );
    }

    return (
        <div className={styles.TabContainer}>
            <div className={styles.MenuItems} role="tablist">
                <div className={styles.UnlockAICA}>
                    {modulePollingList.length > 0 ||
                    Array.from(primaryDataStatusMap.entries()).some(
                        ([k, v]) => k !== 'bankingAndFinancials' && v === DATA_FETCH_STATUS.PROCESSING,
                    ) ? (
                        <div className={styles.FetchingBtn}>
                            Fetching Data
                            <LoadingSpinner color="var(--primary-text-colour)" height="16px" />
                        </div>
                    ) : (
                        <div
                            className={styles.UnlockTooltipWrapper}
                            onMouseEnter={() => shouldDisableUnlock && setShowUnlockTooltip(true)}
                            onMouseLeave={() => setShowUnlockTooltip(false)}
                        >
                            <Button
                                disabled={shouldDisableUnlock}
                                onClick={handleUnlockRefreshClick}
                                icon={
                                    <RefreshIcon
                                        colour={shouldDisableUnlock ? '#CBCBCB' : 'var(--icon-color)'}
                                        style={{ cursor: 'pointer', marginRight: '4px' }}
                                        height="14"
                                        width="14"
                                    />
                                }
                                text={UNLOCK_REFRESH_LABEL}
                                variant="secondary"
                                className={clsx(styles.UnlockRefreshButton, {
                                    [styles.UnlockRefreshButtonDisabled]: shouldDisableUnlock,
                                })}
                            />
                            {showUnlockTooltip && shouldDisableUnlock && (
                                <div className={styles.UnlockTooltip} role="tooltip">
                                    <div className={styles.UnlockTooltipBody}>You cannot unlock AICA Report.</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {/* Desktop only — the mobile layout surfaces Alerts and Connections as
                    cards on Overview instead. Not rendered rather than hidden, so the
                    alert poll and the network fetches never start on mobile. The wrapper
                    owns the group's bottom divider so it still renders when Connections
                    is hidden by config and only Alerts is present. */}
                {!isMobile && (
                    <div className={styles.SidebarChips}>
                        <AlertEngine selectedCompany={selectedCompany} currentTab={currentTab} />
                        {visibilityConfig && reconDrawerVisibility.show && <ReconciliationsChip />}
                        {visibilityConfig && sidebarRelatedNetworkVisibility.show && (
                            <CompaniesNetworkButton
                                companyName={selectedCompany?.orgName}
                                isRelatedPartiesLocked={isTabLocked(TabModule.RELATED_PARTY)}
                            />
                        )}
                    </div>
                )}
                {sidebarTabs.map((tab, i) => (
                    <div className={clsx(styles.MenuItem)} key={i}>
                        <div
                            className={clsx({
                                [styles.InvesteeTabLinksActive]: currentTab === tab?.id,
                                [styles.InvesteeTabLinksLocked]: tab?.id === TabModule.BANKING_AND_FINANCIALS && showBankingNoDataIcon,
                                [styles.Tab]: true,
                            })}
                            key={tab?.id}
                            onClick={() => handleClick(tab)}
                            onMouseEnter={() =>
                                (isTabLocked(tab?.id as string) ||
                                    (tab?.id === TabModule.BANKING_AND_FINANCIALS && (showBankingNoDataIcon || showBankingDot))) &&
                                setShowTooltipIndex(i + 1)
                            }
                            onMouseLeave={() => showTooltipIndex === i + 1 && setShowTooltipIndex(false)}
                        >
                            <div
                                data-toggle="tab"
                                className={clsx({
                                    [styles.InvesteeTabLinks]: true,
                                })}
                            >
                                <span>{tab?.label}</span>
                                {tab?.id === TabModule.BANKING_AND_FINANCIALS && showBankingSpinner && (
                                    <span className={styles.BankingExtractSpinner}>
                                        <LoadingSpinner color="var(--primary-text-colour)" height="14px" />
                                    </span>
                                )}
                                {tab?.id === TabModule.BANKING_AND_FINANCIALS && showBankingDot && (
                                    <span className={styles.BankingPendingDot}>!</span>
                                )}
                                {tab?.id === TabModule.BANKING_AND_FINANCIALS && showBankingNoDataIcon && (
                                    <img src={dataNotAvailable} alt="No bank data available" height="16px" />
                                )}
                                {renderTabStatusIcon(
                                    tab?.id ?? '',
                                    primaryDataStatusMap,
                                    isTabLocked,
                                    primaryModuleList,
                                    modulePollingList,
                                    getPrimaryTabState,
                                    greyLock,
                                    LoadingSpinner,
                                )}

                                {showTooltipIndex === i + 1 && (
                                    <div className={styles.TooltipInfo}>
                                        <div className={styles.TooltipBody}>
                                            {getLockedTabTooltipText(tab?.id as TabModule)}
                                            <img
                                                className={styles.TooltipPointer}
                                                src={'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/left-triangle-white.svg'}
                                                alt=""
                                                height="10px"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}

                <div className={styles.PoweredBy}>
                    <img src={poweredByAica} alt="Powered by AICA" />
                </div>
            </div>

            <TalkToUsModal isOpen={isTalkToUsOpen} onClose={closeTalkToUs} contactInput={contactInput} />
        </div>
    );
}

export default TabSelector;
